import {SheetLocationImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetLocationImpl';
import {MeshCellValueFormatKind} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormatKind';
import {MeshCellTextAlign} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellTextAlign';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {ISheetCellSettings} from '@mintdata/mint-kit/api/functions/sheet/SheetCellSettings';
import {
    FunctionFeature,
    ISheetAPI,
    ISheetLocation,
    ISheetRange,
    ISheetRangeList,
    SheetType,
    SimpleNamingContext
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {SlotId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import bind from 'bind-decorator';
import _debounce from 'lodash/debounce';

export class CellFunctions implements IFunctionContainer
{
    private static _infoTypes = new Set(['address', 'col', 'color', 'contents', 'filename', 'format', 'parentheses',
        'prefix', 'protect', 'row', 'type', 'width']);

    public get namespace()
    {
        return 'cell';
    }

    @Function({
        title: 'address(row, column, sheet)',
        tip: 'Returns a cell reference',
        alias: 'ADDRESS',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'cell reference',
                tip: 'Returns a cell reference',
                type: SheetType.SheetLocation
            }
    })
    public getCellReference(@Param({
                                title: 'sheetAPI',
                                tip: 'API',
                                type: SheetType.SheetAPI
                            }) api:ISheetAPI,
                            @Param({
                                title: 'row',
                                tip: 'Row number',
                                type: Type.Integer
                            }) row:AInteger,
                            @Param({
                                title: 'column',
                                tip: 'Column number',
                                type: Type.Integer
                            }) column:AInteger,
                            @Param({
                                title: 'sheet',
                                tip: 'A string indicating the name of the sheet into which the address points. Current sheet name by default.',
                                type: Type.String,
                                optional: true
                            }) sheet?:AString):ISheetLocation | AError
    {
        const sheetLocation = api.getSheetContext().findCellLocationWithSheetName(
            column.value - 1,
            row.value - 1,
            (sheet) ? sheet.value : null
        );

        if( sheetLocation )
            return sheetLocation;

        return EvalErrors.CellRef;
    }

    @Function({
        title: 'row(cell_reference)',
        tip: 'Returns the row number of a specified cell.',
        alias: 'ROW',
        example: `ROW(C9) - returns 9; ROW() - returns row number of the current cell`,
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'row number',
                tip: 'Returns the row number of a specified cell.',
                type: Type.Integer
            }
    })
    public getCellRowNumber(@Param({
                                title: 'sheetAPI',
                                tip: 'API',
                                type: SheetType.SheetAPI
                            }) api:ISheetAPI,
                            @Param({
                                title: 'cell_reference',
                                tip: 'The cell whose row number will be returned. The cell in which the formula is entered by default.',
                                type: SheetType.SheetLocation,
                                optional: true
                            }) cell_reference:ISheetLocation):AInteger | AError
    {
        const location:ISheetLocation = (cell_reference) ? cell_reference : api.getSheetContext().getCellLocation();
        return AInteger.fromNumber(location.rowIndex + 1);
    }

    @Function({
        title: 'column(cell_reference)',
        tip: 'Returns the column number of a specified cell.',
        alias: 'COLUMN',
        example: `COLUMN(C9) - returns 3; COLUMN() - returns column number of the current cell`,
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'column number',
                tip: 'Returns the column number of a specified cell.',
                type: Type.Integer
            }
    })
    public getCellColumnNumber(@Param({
                                   title: 'sheetAPI',
                                   tip: 'API',
                                   type: SheetType.SheetAPI
                               }) api:ISheetAPI,
                               @Param({
                                   title: 'cell_reference',
                                   tip: 'The cell whose column number will be returned. The cell in which the formula is entered by default.',
                                   type: SheetType.SheetLocation,
                                   optional: true
                               }) cell_reference:ISheetLocation):AInteger | AError
    {
        const location:ISheetLocation = (cell_reference) ? cell_reference : api.getSheetContext().getCellLocation();
        return AInteger.fromNumber(location.columnIndex + 1);
    }

    @Function({
        title: 'isError(cellReference)',
        alias: 'IS_ERROR',
        example: 'IS_ERROR(A1)',
        tip: 'Returns TRUE if the cell value is an error. Otherwise returns FALSE',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                type: Type.Boolean,
                title: 'result',
                tip: 'Returns TRUE if the cell value is an error. Otherwise returns FALSE'
            }
    })
    public isError(@Param({
                       title: 'sheetAPI',
                       tip: 'API',
                       type: SheetType.SheetAPI
                   })
                       api:ISheetAPI,
                   @Param({
                       title: 'cellReference',
                       tip: 'The cell whose value to check for error',
                       type: SheetType.SheetLocation
                   })
                       location:ISheetLocation):IValue
    {
        const cellValue = api.getSheetContext().getCellValue(location);

        return ABoolean.fromBoolean(cellValue.isError);
    }

    @Function({
        title: 'isLoading(range)',
        alias: 'IS_LOADING',
        example: 'IS_LOADING(A1:B1)',
        tip: 'Returns TRUE if any cell in a range is loading. Otherwise returns FALSE',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                type: Type.Boolean,
                title: 'result',
                tip: 'Returns TRUE if any cell in a range is loading. Otherwise returns FALSE'
            }
    })
    public isLoading(@Param({
                         title: 'sheetAPI',
                         tip: 'API',
                         type: SheetType.SheetAPI
                     })
                         api:ISheetAPI,
                     @Param({
                         title: 'range',
                         tip: 'Range to perform function in',
                         type: SheetType.SheetRangeList,
                         repeatable: true
                     })
                         rangeLists:ISheetRangeList[]):IValue
    {
        const ctx = api.getSheetContext();

        return ABoolean.fromBoolean(rangeLists.some((rangeList:ISheetRangeList) =>
        {
            return rangeList.ranges.some((range:ISheetRange) =>
            {
                if( range )
                {
                    const rangeIterator = ctx.getRangeIterator(range);
                    const numCells = rangeIterator.numCells;

                    for( let cellOffset = 0; cellOffset < numCells; cellOffset++ )
                    {
                        if( ctx.isCellLoading(rangeIterator.getNthCellLocation(cellOffset)) )
                        {
                            return true;
                        }
                    }
                }

                return false;
            });
        }));
    }

    @Function({
        title: 'getCell(cellReference)',
        alias: 'GET_CELL',
        tip: 'Returns the value of a cell',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'result',
                tip: 'The value contained in the cell'
            }
    })
    public getCell(@Param({
                       title: 'sheetAPI',
                       tip: 'API',
                       type: SheetType.SheetAPI
                   })
                       api:ISheetAPI,
                   @Param({
                       title: 'cellReference',
                       tip: 'The cell whose value to return',
                       type: SheetType.SheetLocation
                   })
                       location:ISheetLocation):IValue
    {
        return api.getSheetContext().getCellValue(location);
    }

    @Function({
        title: 'appendFromFrame(cellReference, value)',
        tip: 'Append contents of the frame below the specified cell',
        alias: 'APPEND',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'true',
                tip: 'This function always returns true',
                type: Type.Boolean
            }
    })
    public async appendFromFrame(@Param({
                                     title: 'sheetAPI',
                                     tip: 'API',
                                     type: SheetType.SheetAPI
                                 })
                                     api:ISheetAPI,
                                 @Param({
                                     title: 'value',
                                     tip: 'The Frame with values',
                                     type: SheetType.SheetLocation
                                 })
                                     value:ISheetLocation,
                                 @Param({
                                     title: 'cellReference',
                                     tip: 'The cell where to insert frame contents',
                                     type: SheetType.SheetLocation
                                 })
                                     location:ISheetLocation):Promise<ABoolean>
    {
        await api.getSheetContext().addRowsBelow(location, api.getSheetContext().getCellValue(value) as IFrame);
        return ABoolean.true();
    }

    @Function({
        title: 'appendFromRange(range, cellReference)',
        tip: 'Append contents of the cell range below the specified cell',
        alias: 'APPEND_RANGE',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'true',
                tip: 'This function always returns true',
                type: Type.Boolean
            }
    })
    public async appendFromRange(@Param({
                                     title: 'sheetAPI',
                                     tip: 'API',
                                     type: SheetType.SheetAPI
                                 })
                                     api:ISheetAPI,
                                 @Param({
                                     title: 'range',
                                     tip: 'The range with values',
                                     type: SheetType.SheetRange
                                 })
                                     range:ISheetRange,
                                 @Param({
                                     title: 'cellReference',
                                     tip: 'The cell where to insert frame contents',
                                     type: SheetType.SheetLocation
                                 })
                                     location:ISheetLocation,
                                 @Param({
                                     title: 'times',
                                     tip: 'The number of times the range with values should be inserted',
                                     type: Type.Number,
                                     optional: true
                                 })
                                     times?:ANumber):Promise<ABoolean>
    {
        await api.getSheetContext().copyRangeWithFormulas(range, location, times ? times.value : 1);
        return ABoolean.true();
    }

    @Function({
        title: 'Range(cellReference, value)',
        tip: 'Defines range - finds all cells in the column that contains data starting from given cell and get their values in a single frame',
        alias: 'RANGE_VALUES',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'Range',
                tip: 'Range of the cells',
                type: SheetType.SheetRange
            }
    })
    public rangeValues(@Param({
                           title: 'sheetAPI',
                           tip: 'API',
                           type: SheetType.SheetAPI
                       })
                           api:ISheetAPI,
                       @Param({
                           title: 'begin',
                           tip: 'Cell to start from',
                           type: SheetType.SheetLocation
                       }) location:ISheetLocation):IFrame
    {
        return api.getSheetContext().readValuesUntilEmptyCell(location);
    }

    @Function({
        title: 'Range(cellReference, [offset_in_column], [num_items], [column_to_use])',
        tip: 'Defines range - finds all cells in the column that contains data starting from given cell and get their values in a single frame',
        alias: 'RANGE',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'Range',
                tip: 'Range of the cells',
                type: SheetType.SheetRange
            }
    })
    public range(@Param({
                     title: 'sheetAPI',
                     tip: 'API',
                     type: SheetType.SheetAPI
                 })
                     api:ISheetAPI,
                 @Param({
                     title: 'begin',
                     tip: 'Range to start from',
                     type: SheetType.SheetRange
                 }) range:ISheetRange,
                 @Param({
                     title: 'offsetInColumn',
                     tip: '?',
                     type: Type.Integer,
                     optional: true
                 }) offset_in_column:AInteger,
                 @Param({
                     title: 'numItems',
                     tip: '?',
                     type: Type.Integer,
                     optional: true
                 }) num_items:AInteger,
                 @Param({
                     title: 'columnToUse',
                     tip: '?',
                     type: Type.Integer,
                     optional: true
                 }) column_to_use:AInteger):IValue | ISheetRange
    {
        return api.getSheetContext().defineRangeBoundary(
            range,
            offset_in_column && offset_in_column.value,
            num_items && num_items.value,
            column_to_use && column_to_use.value
        );
    }

    @Function({
        title: 'SET_CELL(rangeReference, formula)',
        alias: 'SET_CELL',
        lazyOnly: true,
        tip: 'Sets a formula to a cell or range of cells',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'setCell',
                tip: 'This function always returns true',
                type: SheetType.AnyIValue
            }
    })
    public async setCell(@Param({
                             title: 'sheetAPI',
                             tip: 'API',
                             type: SheetType.SheetAPI
                         })
                             api:ISheetAPI,
                         @Param({
                             title: 'rangeReference',
                             tip: 'Range of cells to be set',
                             type: SheetType.SheetRange
                         }) range:ISheetRange,
                         @Param({
                             title: 'formula',
                             tip: 'The formula to set',
                             type: SheetType.AnyIValue
                         })
                             formula:IValue):Promise<IValue>
    {
        for( let col:number = range.from.columnIndex; col <= range.to.columnIndex; col++ )
        {
            for( let row:number = range.from.rowIndex; row <= range.to.rowIndex; row++ )
            {
                const location:ISheetLocation = new SheetLocationImpl(range.sheetId, col, row);

                await api.getSheetContext().setCellFormula(location, formula);
            }
        }

        return ABoolean.true();
    }

    @Function({
        title: 'SET_CELL_VALUE(rangeReference, value)',
        alias: 'SET_CELL_VALUE',
        lazyOnly: true,
        tip: 'Sets a value to a cell or range of cells',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'setCellValue',
                tip: 'This function always returns true',
                type: SheetType.AnyIValue
            }
    })
    public async setCellValue(@Param({
                                  title: 'sheetAPI',
                                  tip: 'API',
                                  type: SheetType.SheetAPI
                              })
                                  api:ISheetAPI,
                              @Param({
                                  title: 'rangeReference',
                                  tip: 'Range of cells to be set',
                                  type: SheetType.SheetRange
                              }) range:ISheetRange,
                              @Param({
                                  title: 'value',
                                  tip: 'The value to set',
                                  type: SheetType.AnyIValue
                              })
                                  value:IValue):Promise<IValue>
    {
        for( let col:number = range.from.columnIndex; col <= range.to.columnIndex; col++ )
        {
            for( let row:number = range.from.rowIndex; row <= range.to.rowIndex; row++ )
            {
                const location:ISheetLocation = new SheetLocationImpl(range.sheetId, col, row);

                await api.getSheetContext().setCellValue(location, value);
            }
        }

        return ABoolean.true();
    }

    @Function({
        title: 'CLEAR_CELL(rangeReference)',
        alias: 'CLEAR_CELL',
        example: 'CLEAR_CELL(A1) or CLEAR_CELL(A1:B1)',
        tip: 'Clears a formula from a cell or range of cells',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'clearCell',
                tip: 'This function always returns true',
                type: Type.Boolean
            }
    })
    public async clearCell(@Param({
                               title: 'sheetAPI',
                               tip: 'API',
                               type: SheetType.SheetAPI
                           })
                               api:ISheetAPI,
                           @Param({
                               title: 'rangeReference',
                               tip: 'Range of cells to be cleared',
                               type: SheetType.SheetRange
                           }) range:ISheetRange):Promise<ABoolean>
    {
        await api.getSheetContext().clearRange(range.from, range.to, true);
        return ABoolean.true();
    }

    @Function({
        title: 'CLEAR_CELL_VALUE(rangeReference)',
        alias: 'CLEAR_CELL_VALUE',
        example: 'CLEAR_CELL_VALUE(A1) or CLEAR_CELL_VALUE(A1:B1)',
        tip: 'Clears a value from a cell or range of cells',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'clearCellValue',
                tip: 'This function always returns true',
                type: Type.Boolean
            }
    })
    public async clearCellValue(@Param({
                                    title: 'sheetAPI',
                                    tip: 'API',
                                    type: SheetType.SheetAPI
                                })
                                    api:ISheetAPI,
                                @Param({
                                    title: 'rangeReference',
                                    tip: 'Range of cells to be cleared',
                                    type: SheetType.SheetRange
                                }) range:ISheetRange):Promise<ABoolean>
    {
        for( let col:number = range.from.columnIndex; col <= range.to.columnIndex; col++ )
        {
            for( let row:number = range.from.rowIndex; row <= range.to.rowIndex; row++ )
            {
                const location:ISheetLocation = new SheetLocationImpl(range.sheetId, col, row);

                await api.getSheetContext().setCellValue(location, AEmpty.Value);
            }
        }

        return ABoolean.true();
    }

    @Function({
        title: 'DELETE_MAIN_ROWS(rangeReference)',
        alias: 'DELETE_MAIN_ROWS',
        example: 'DELETE_MAIN_ROWS(A1:B1, false)',
        lazyOnly: true,
        tip: 'Deletes rows in the range',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'deleteMainRows',
                tip: 'This function always returns true',
                type: Type.Boolean
            }
    })
    public async deleteMainRows(@Param({
                                    title: 'sheetAPI',
                                    tip: 'API',
                                    type: SheetType.SheetAPI
                                })
                                    api:ISheetAPI,
                                @Param({
                                    title: 'rangeReference',
                                    tip: 'Rows to be deleted',
                                    type: SheetType.SheetRange
                                }) range:ISheetRange,
                                @Param({
                                    title: 'keepBindings',
                                    tip: 'If bindings should be kept. Default value is false',
                                    type: Type.Boolean,
                                    optional: true
                                })
                                    keepBindings:ABoolean):Promise<ABoolean>
    {
        await api.getSheetContext().deleteRows(range.from, range.to, keepBindings && keepBindings.value);
        return ABoolean.true();
    }

    @Function({
        title: 'DELETE_MAIN_COLUMNS(rangeReference)',
        alias: 'DELETE_MAIN_COLUMNS',
        example: 'DELETE_MAIN_COLUMNS(A1:B1, false)',
        lazyOnly: true,
        tip: 'Deletes columns in the range',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'deleteMainColumns',
                tip: 'This function always returns true',
                type: Type.Boolean
            }
    })
    public async deleteMainColumns(@Param({
                                       title: 'sheetAPI',
                                       tip: 'API',
                                       type: SheetType.SheetAPI
                                   })
                                       api:ISheetAPI,
                                   @Param({
                                       title: 'rangeReference',
                                       tip: 'Columns to be deleted',
                                       type: SheetType.SheetRange
                                   }) range:ISheetRange,
                                   @Param({
                                       title: 'keepBindings',
                                       tip: 'If bindings should be kept. Default value is false.',
                                       type: Type.Boolean,
                                       optional: true
                                   })
                                       keepBindings:ABoolean):Promise<ABoolean>
    {
        await api.getSheetContext().deleteColumns(range.from, range.to, keepBindings && keepBindings.value);
        return ABoolean.true();
    }

    @Function({
        title: 'DEBOUNCE(range, wait)',
        alias: 'DEBOUNCE',
        lazyOnly: true,
        tip: 'Delays executing formulas in the provided cells until after wait milliseconds have elapsed since the last time DEBOUNCE function was invoked',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'result',
                tip: 'Always returns true',
                type: Type.Boolean
            }
    })
    @bind
    public async debounce(@Param({
                              title: 'sheetAPI',
                              tip: 'API',
                              type: SheetType.SheetAPI
                          })
                              api:ISheetAPI,
                          @Param({
                              title: 'range',
                              tip: 'Range of cells for evaluating',
                              type: SheetType.SheetRangeList
                          })
                              ranges:ISheetRangeList,
                          @Param({
                              title: 'wait',
                              tip: 'The number of milliseconds to delay',
                              type: Type.Number,
                              optional: true
                          })
                              wait?:ANumber):Promise<ABoolean|AError>
    {
        const ctx = api.getSheetContext();

        if( ranges.ranges.some(range => range.contains(ctx.getCellLocation()) ? true: false) )
            return new AError(`#CircularRef`);

        wait = wait || ANumber.fromNumber(250);

        const reevaluateRangeDebounced = this._debounce(api, this._reevaluateRange, wait.value);

        await reevaluateRangeDebounced(api, [ranges]);

        return ABoolean.true();
    }

    private _debouncedFns:Map<SlotId, {fn:(...args:any[]) => any, wait:number}> = new Map();

    private _debounce(api:ISheetAPI, fn:(...args:any[]) => any, wait:number)
    {
        const slotId:SlotId = api.getSheetContext().getSlotId();

        const existing = this._debouncedFns.get(slotId);

        if( !existing || existing.wait !== wait )
            this._debouncedFns.set(slotId, {fn: _debounce(fn, wait), wait});

        return this._debouncedFns.get(slotId).fn;
    }

    @Function({
        title: 'DO(range)',
        alias: 'DO',
        lazyOnly: true,
        tip: 'Execute formulas in the provided cells',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'perform',
                tip: 'Execute function in provided cells',
                type: Type.Boolean
            }
    })
    @bind
    public async perform(@Param({
                             title: 'sheetAPI',
                             tip: 'API',
                             type: SheetType.SheetAPI
                         })
                             api:ISheetAPI,
                         @Param({
                             title: 'range',
                             tip: 'Range to perform function in',
                             type: SheetType.SheetRangeList,
                             repeatable: true
                         }) rangeLists:ISheetRangeList[]):Promise<ABoolean|AError>
    {
        const ctx = api.getSheetContext();

        if( rangeLists.some(range => range.ranges.some(range => range.contains(ctx.getCellLocation()) ? true: false)) )
            return new AError(`#CircularRef`);

        await this._reevaluateRange(api, rangeLists);

        return ABoolean.true();
    }

    @Function({
        title: 'DO_PARALLEL(range)',
        alias: 'DO_PARALLEL',
        lazyOnly: true,
        tip: 'Execute parallel formulas in the provided cells',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'perform',
                tip: 'Execute function in provided cells',
                type: Type.Boolean
            }
    })
    public performAsync(@Param({
                            title: 'sheetAPI',
                            tip: 'API',
                            type: SheetType.SheetAPI
                        })
                            api:ISheetAPI,
                        @Param({
                            title: 'range',
                            tip: 'Range to perform function in',
                            type: SheetType.SheetRangeList,
                            repeatable: true
                        }) rangeLists:ISheetRangeList[]):ABoolean|AError
    {
        const ctx = api.getSheetContext();

        if( rangeLists.some(range => range.ranges.some(range => range.contains(ctx.getCellLocation()) ? true: false)) )
            return new AError(`#CircularRef`);

        for( let i = 0; i < rangeLists.length; i++ )
        {
            const rangeList:ISheetRangeList = rangeLists[i];

            for( let j = 0; j < rangeList.ranges.length; j++ )
            {
                const range:ISheetRange = rangeList.ranges[j];

                if( !range )
                    continue;

                const rangeIterator = ctx.getRangeIterator(range);
                const numCells = rangeIterator.numCells;

                for( let c = 0; c < numCells; c++ )
                {
                    try
                    {
                        const location:ISheetLocation = rangeIterator.getNthCellLocation(c);

                        const donePromise = ctx.reEvaluateLazyCell(location);

                        donePromise.catch((reason) =>
                        {
                            console.error(`DO_PARALLEL ${location} => ${reason}`);
                        });
                    }
                    catch( e )
                    {
                        console.error(`DO_PARALLEL ${location} => ${e}`);
                    }
                }
            }
        }

        return ABoolean.true();
    }

    @Function({
        title: 'DO_IF_CHANGED(cell reference, range)',
        alias: 'DO_IF_CHANGED',
        tip: 'Execute formulas in the provided cells if value in referenced cell was changed',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                title: 'perform',
                tip: 'Execute function in provided cells',
                type: SheetType.AnyIValue
            }
    })
    @bind
    public async performIf(@Param({
                               title: 'sheetAPI',
                               tip: 'API',
                               type: SheetType.SheetAPI
                           }) api:ISheetAPI,
                           @Param({
                               title: 'cellReference',
                               tip: 'The cell whose value to check',
                               type: SheetType.SheetLocation
                           }) location:ISheetLocation,
                           @Param({
                               title: 'range',
                               tip: 'Range to perform function in',
                               type: SheetType.SheetRangeList,
                               repeatable: true
                           }) rangeLists:ISheetRangeList[]):Promise<IValue>
    {
        const ctx = api.getSheetContext();
        const oldCellValue:IValue = ctx.getCellCurrentValue();
        const cellValue:IValue = ctx.getCellValue(location);

        if( cellValue && (!oldCellValue || !cellValue.isEqualTo(oldCellValue)) )
            await this._reevaluateRange(api, rangeLists);

        return cellValue;
    }

    @bind
    private async _reevaluateRange(api:ISheetAPI, rangeLists:ISheetRangeList[]):Promise<void>
    {
        const ctx = api.getSheetContext();

        for( let i = 0; i < rangeLists.length; i++ )
        {
            const rangeList:ISheetRangeList = rangeLists[i];

            for( let j = 0; j < rangeList.ranges.length; j++ )
            {
                const range:ISheetRange = rangeList.ranges[j];

                if( !range )
                    continue;

                const rangeIterator = ctx.getRangeIterator(range);
                const numCells = rangeIterator.numCells;

                for( let c = 0; c < numCells; c++ )
                {
                    try
                    {
                        const location:ISheetLocation = rangeIterator.getNthCellLocation(c);

                        await ctx.reEvaluateLazyCell(location);
                    }
                    catch( e )
                    {
                        console.error(`DO ${location} => ${e}`);
                    }
                }
            }
        }
    }

    @Function({
        title: 'TRANSPOSE(inputFrame [, wrapInFrame = FALSE])',
        tip: 'Transposes the rows and columns of a range of cells. If wrapInFrame TRUE, the result will be a Frame, otherwise - result will be inlined.',
        alias: 'TRANSPOSE',
        example: 'TRANSPOSE(A1:B12)',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'Transposition',
                tip: 'The result of a transposition on a range of size m rows by n columns is therefore n rows by m columns.'
            }
    })

    @bind
    public async transpose(@Param({
                               title: 'sheetAPI',
                               tip: 'API',
                               type: SheetType.SheetAPI
                           }) api:ISheetAPI,
                           @Param({
                               title: 'range',
                               tip: 'The range whose rows and columns will be swapped.',
                               type: Type.Frame
                           }) inputFrame:IFrame,
                           @Param({
                               title: 'wrapInFrame',
                               tip: 'Wrap result in Frame, instead of inlining it',
                               type: Type.Boolean,
                               optional: true
                           }) wrapInFrame:ABoolean):Promise<IValue>
    {
        const namingContext = new SimpleNamingContext();

        const fields = inputFrame.getFields();
        const records = inputFrame.getRecords();

        const newFields:FrameField[] = [];
        const newRecords:IValue[][] = [];

        for( let fieldIndex = 0; fieldIndex < fields.length; fieldIndex++ )
        {
            const newRecord:IValue[] = [];

            for( let recordIndex = 0; recordIndex < records.length; recordIndex++ )
            {
                const value = records[recordIndex].getCellAtOffset(fieldIndex);

                if( newFields[recordIndex] === undefined && value )
                {
                    newFields[recordIndex] = new FrameField(namingContext.getNextColumnName(), value.getType());
                }

                newRecord.push(value);
            }

            newRecords.push(newRecord);
        }

        for( let newFieldIndex = 0; newFieldIndex < records.length; newFieldIndex++ )
        {
            if( newFields[newFieldIndex] === undefined )
            {
                newFields[newFieldIndex] = new FrameField(namingContext.getNextColumnName(), Type.String);
            }
        }

        const resultFrame = Frames.fromFieldsAndData(newFields, newRecords);

        if( wrapInFrame && wrapInFrame.value )
            return resultFrame;

        const ctx = api.getSheetContext();

        if( ctx.isArrayFormula() )
            return resultFrame;

        const start = ctx.getCellLocation();
        for( let rowShift = 0; rowShift < resultFrame.getRecords().length; rowShift++ )
        {
            let record = resultFrame.getRecords()[rowShift];
            for( let colShift = 0; colShift < record.getCells().length; colShift++ )
            {
                if( colShift === 0 && rowShift === 0 ) continue; //should not replace value in the cell we are
                                                                 // starting from

                let cell = record.getCellAtOffset(colShift);
                const locationToSetValueTo = new SheetLocationImpl(start.sheetId, start.columnIndex + colShift, start.rowIndex + rowShift);
                await ctx.setCellFormula(locationToSetValueTo, cell);
            }

        }

        return resultFrame.getCellByOffset(0, 0);
    }

    @Function({
        title: 'ISBLANK(value)',
        alias: 'ISBLANK',
        tip: 'Returns TRUE if a value isn\'t null and not an empty string',
        example: 'ISBLANK(A1)',
        returns:
            {
                type: Type.Boolean,
                title: 'result',
                tip: 'TRUE if a value isn\'t null and not an empty string'
            }
    })
    @bind
    public isBlank(@Param({
                       title: 'sheetAPI',
                       tip: 'API',
                       type: SheetType.SheetAPI
                   }) api:ISheetAPI,
                   @Param({
                       title: 'value',
                       tip: 'Value to check',
                       type: SheetType.AnyIValue
                   }) value:IValue):ABoolean | AError
    {
        const blank = Type.isInstanceOf(value, Type.Empty) ||
            (Type.isInstanceOf(value, Type.String) && (value as AString).value.length === 0);
        return new ABoolean(blank);
    }

    @Function({
        title: 'IS_NULL(value)',
        alias: 'IS_NULL',
        tip: 'Returns TRUE if a value isn\'t null',
        example: 'IS_NULL(A1)',
        returns:
            {
                type: Type.Boolean,
                title: 'result',
                tip: 'TRUE when a value is empty. But FALSE, if it contains empty string'
            }
    })
    @bind
    public isNull(@Param({
                      title: 'sheetAPI',
                      tip: 'API',
                      type: SheetType.SheetAPI
                  }) api:ISheetAPI,
                  @Param({
                      title: 'value',
                      tip: 'Value to check',
                      type: SheetType.AnyIValue
                  }) value:IValue):ABoolean | AError
    {
        return new ABoolean(
            Type.isInstanceOf(value, Type.Empty)
        );
    }

    @Function({
        title: 'TYPE_OF(value, type)',
        alias: 'TYPE_OF',
        tip: 'Returns a type name for the type of the specified value',
        example: 'TYPE_OF("123")',
        returns:
            {
                type: Type.String,
                title: 'typeName',
                tip: 'Type name'
            }
    })
    @bind
    public typeOf(@Param({
        title: 'value',
        tip: 'Value',
        type: SheetType.AnyIValue
    }) value:IValue):AString
    {
        return new AString(value.getType().kind);
    }

    @Function({
        title: 'CAST(value, type)',
        alias: 'CAST',
        tip: 'Converts value to specified type',
        example: 'CAST("123", "number") | ' +
        'CAST(B2, "currency")',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'result',
                tip: 'Casted value'
            }
    })
    @bind
    public cast(@Param({
                    title: 'value',
                    tip: 'Value to cast',
                    type: SheetType.AnyIValue
                }) value:IValue,
                @Param({
                    title: 'type',
                    tip: 'Type name to cast to. ' +
                    'Available types: "number" | "boolean" | "currency" ' +
                    '| "integer" | "binary" | "datetime" | "frame" | "time" | "string"',
                    type: Type.String
                }) typeName:AString):IValue | AError
    {
        const typeOrError = this._typeFromName(typeName.value);
        if( Type.isError(typeOrError) )
            return typeOrError as AError;

        const type = typeOrError as Type;
        return CoercionUtils.coerce(value, type, null);
    }

    private _typeFromName(typeName:string):Type | AError
    {
        const type = Type.fromTypeName(typeName);
        if( type )
            return type;

        const commonNamingErrors = [
            ['binaryData', 'binary', 'binary data', 'binary_data'],
            ['boolean', 'bool'],
            ['datetime', 'date time', 'date_time'],
            ['integerEnum', 'integer enum', 'integerenum', 'integer_enum'],
            ['stringEnum', 'string enum', 'stringenum', 'string_enum'],
            ['string', 'str']
        ];

        for( const alternatives of commonNamingErrors )
        {
            if( alternatives.indexOf(typeName) > 0 )
                return new AError(`#INVALID_TYPE_NAME: "${typeName}", did you mean "${alternatives[0]}" ?`);
        }

        return new AError(`#INVALID_TYPE_NAME: ${typeName}`);
    }

    @Function({
        title: 'event()',
        tip: 'Returns the event information of the cell (if component event is bound to the cell)',
        alias: 'E',
        lazyOnly: true,
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                type: Type.Frame,
                title: 'event',
                tip: 'Event as a frame'
            }
    })
    public event(@Param({
        title: 'sheetAPI',
        tip: 'API',
        type: SheetType.SheetAPI
    }) api:ISheetAPI):IFrame | AError
    {
        return api.getSheetContext().getEventFrame() || EvalErrors.NotAnEvent;
    }

    @Function({
        title: 'E_ITEM_INDEX()',
        tip: 'Returns the index of an item when the item is inside a repeater. When the item is not in a repeater, always returns “1”.',
        alias: 'E_ITEM_INDEX',
        lazyOnly: true,
        example: 'E_ITEM_INDEX()',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                type: Type.Number,
                title: 'repeated_item_index',
                tip: 'repeated_item_index from Event'
            }
    })
    public e_item_index(@Param({
        title: 'sheetAPI',
        tip: 'API',
        type: SheetType.SheetAPI
    }) api:ISheetAPI):ANumber | AError
    {
        const frame:IFrame = api.getSheetContext().getEventFrame();

        if( !frame ) return EvalErrors.NotAnEvent;

        const records = frame.getRecords();

        if( !records.length ) return EvalErrors.NotAnEvent;

        const repeated_item_index = records[0].getCell('repeated_item_index');

        if( repeated_item_index.isEmpty ) return ANumber.fromNumber(1);

        return repeated_item_index as ANumber;
    }

    @Function({
        title: 'CELL(info_type, [reference])',
        tip: 'Returns information about the formatting, location, or contents of a cell',
        example: 'CELL("format",A1)',
        alias: 'CELL',
        returns:
            {
                type: Type.String,
                title: 'Cell',
                tip: 'Information about cell'
            }
    })
    public cell(@Param({
                    title: 'sheetAPI',
                    tip: 'API',
                    type: SheetType.SheetAPI
                }) api:ISheetAPI,
                @Param({
                    title: 'info_type',
                    tip: 'Info Type. A text value that specifies what type of cell information you want to return',
                    type: Type.String
                }) infoType:AString,
                @Param({
                    title: 'reference',
                    tip: 'Cell reference',
                    type: SheetType.SheetLocation
                }) cellReference:ISheetLocation):IValue
    {
        const infoTypeValue = infoType.value;
        if( !CellFunctions._infoTypes.has(infoTypeValue) ) return EvalErrors.Value;
        const cellValue = api.getSheetContext().getCellValue(cellReference);
        const cellSettings = api.getSheetContext().getCellSettings(cellReference);

        switch( infoTypeValue )
        {
            case 'address':
                return new AString(cellReference.toA1Location());
            case 'col':
                return new AInteger(cellReference.columnIndex + 1);
            case 'contents':
                return cellValue;
            case 'filename':
                const designName = api.getSheetContext().getDesignName();
                return new AString(designName.replace(' ', '_').toLowerCase());
            case 'format':
                if( undefined == cellSettings || null == cellSettings )
                {
                    return new AString('G');
                }
                return new AString(cellSettings.valueFormat.formatCode());
            case 'parentheses':
                if( undefined == cellSettings || null == cellSettings )
                {
                    return new AInteger(0);
                }
                else if( cellSettings && (cellSettings.valueFormat.kind == MeshCellValueFormatKind.Accounting ||
                        cellSettings.valueForma.kindt == MeshCellValueFormatKind.Financial) )
                {
                    return new AInteger(1);
                }
                else
                {
                    return new AInteger(0);
                }
            case 'prefix':
                return new AString(CellFunctions._formCellPrefix(cellSettings));
            case 'protect':
                return new AInteger(0); //No locked cells ATM
            case 'row':
                return new AInteger(cellReference.rowIndex + 1);
            case 'type':
                if( Type.isInstanceOf(cellValue, Type.Empty) )
                {
                    return new AString('b');
                }
                else if( Type.isInstanceOf(cellValue, Type.String) )
                {
                    return new AString('l');
                }
                else
                {
                    return new AString('v');
                }
            case 'width':
                return new AInteger(api.getSheetContext().getRowWidth(cellReference));
            default:
                return EvalErrors.Value;
        }
    }

    private static _formCellPrefix(cellSettings:ISheetCellSettings):string
    {
        if( undefined == cellSettings ) return '';
        const textAlign = cellSettings.textAlign;
        switch( textAlign )
        {
            case MeshCellTextAlign.Center:
                return '^';
            case MeshCellTextAlign.Left:
                return '\'';
            case MeshCellTextAlign.Right:
                return '"';
            default:
                return ''; //TODO: Fill?

        }
    }
}
